import {Component, OnInit} from '@angular/core';
import {TreeDragDropService} from 'primeng/api';
import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {XxFormLogicPlaygroundService} from '../../../service/api/xx-form-logic-playground.service';
import {
    XxFormLogicModeName,
    XxFormLogicRuleDto,
    XxFormLogicScenarioDto
} from '../../../model/dto/api/xx-form-logic-scenario.dto';

/**
 * One of the two instance families a scenario is installed on.
 *
 * The distinction is the whole point of offering both: `xx.XxGenericTest` HAS a Java
 * `XxGenericTestForm`, `xx.XxGenericTestNoForm` has none. Form logics run AFTER the Java form
 * object where one exists, so switching between the two shows that configuration beats code —
 * and covers the 30-of-52 instance families that own no `*Form` class at all.
 */
interface XxFormLogicFamily {
    /** ObjectType alias passed to `<mvs-crud-object>`. */
    objectType: string;
    /** German label for the toggle. */
    label: string;
    /** German one-liner explaining what this route proves. */
    hint: string;
}

/** German label per mode name — mirrors the backend `@UiEnumValueInfo(uiLabel)`. */
const MODE_LABEL: Record<XxFormLogicModeName, string> = {
    inactive: 'Inaktiv',
    active: 'Aktiv',
    strict: 'Streng'
};

/**
 * The subset of PrimeNG `p-tag` severities used here. Typed as the literal union rather than
 * `string`, because `[severity]` is typed and a plain `string` does not assign to it.
 */
type XxTagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

/** PrimeNG severity per mode, so the master switch is readable at a glance. */
const MODE_SEVERITY: Record<XxFormLogicModeName, XxTagSeverity> = {
    inactive: 'secondary',
    active: 'success',
    strict: 'warn'
};

/**
 * "Formular-Logik" playground page (route `xx/form-logic`).
 *
 * A hands-on catalogue of `cc.FormLogic` scenarios on the `xx` test entities: pick a scenario on
 * the left, read what it should do, and see it happen in a real create form on the right. Each
 * scenario is its own `XxGenericTestType` row, because form logics resolve per
 * `(objectType, genericTypeId, hook)` — selecting the type is therefore what selects the scenario,
 * with no interference from the other rules in the catalogue.
 *
 * The form is a plain `<mvs-crud-object>` in create mode with the scenario's type pre-set through
 * `defaultCreateDto`. Nothing on this page renders fields itself: everything visible — hidden
 * fields, required markers, read-only controls, filtered value lists, pre-filled defaults and the
 * refresh round trip — is the framework reacting to the seeded configuration.
 *
 * The catalogue and the expectation texts come from the backend
 * (`XxFormLogicSeedDefinition`), so the page never restates them and cannot drift from what is
 * actually seeded.
 */
@Component({
    selector: 'mvs-xx-form-logic',
    templateUrl: './xx-form-logic.component.html',
    styleUrls: ['./xx-form-logic.component.scss'],
    standalone: false,
    // Required by the lg.Logic object component this page embeds: its code editor INJECTS
    // TreeDragDropService without providing it, so it relies on an ancestor to do so. Without this the
    // embedded PrimeNG tree throws "Cannot read properties of null (reading 'dragStart$')" on init and
    // the whole logic view fails to render. The lg pages that host the editor provide it the same way.
    providers: [TreeDragDropService]
})
export class XxFormLogicComponent implements OnInit {

    /** True once the catalogue has been loaded (successfully or not). */
    initialized = false;

    /** True while the catalogue is loading or the seed is running. */
    busy = false;

    /** German error message for the load / seed step, or null when none. */
    errorMessage: string | null = null;

    /** German success message after a seed run, or null when none. */
    seedMessage: string | null = null;

    /** The scenario catalogue as delivered by the backend. */
    scenarios: XxFormLogicScenarioDto[] = [];

    /** Currently selected scenario, or null before the first selection. */
    selected: XxFormLogicScenarioDto | null = null;

    /** The two instance families the same scenario can be rendered through. */
    readonly families: XxFormLogicFamily[] = [
        {
            objectType: 'xx.XxGenericTest',
            label: 'Mit Java-Formularklasse',
            hint: 'Diese Familie hat eine XxGenericTestForm. Die Formular-Logik läuft NACH dieser '
                + 'Klasse — Konfiguration schlägt also Code.'
        },
        {
            objectType: 'xx.XxGenericTestNoForm',
            label: 'Ohne Java-Formularklasse',
            hint: 'Diese Familie hat keine Formularklasse. Hier ist die Formular-Logik der einzige '
                + 'dynamische Schritt — so wie bei 30 der 52 Instanzfamilien.'
        }
    ];

    /** Currently selected instance family (the with-form route by default). */
    activeFamily: XxFormLogicFamily = this.families[0];

    /**
     * The DTO handed to `<mvs-crud-object>` as `defaultCreateDto`. Carries the scenario's `typeId`
     * as `typeDtoId`, which is how a generic template form is told which type it is for — and
     * therefore which form logics apply.
     */
    createDto: DtoDetail | null = null;

    /**
     * Bumped on every scenario / family change. The template tracks a single-element list by this
     * value, so Angular DESTROYS and rebuilds the form instead of re-using it. Without that the
     * already-built form would keep the previous scenario's field states, and a scenario that only
     * REMOVES a restriction would appear not to work.
     */
    formInstanceKey = 0;

    constructor(protected playgroundService: XxFormLogicPlaygroundService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** Initialize Component. */
    initComponent(): void {
        this.refreshComponent();
    }

    /** Refresh Component: (re-)load the scenario catalogue. */
    refreshComponent(): void {

        this.busy = true;
        this.errorMessage = null;

        this.playgroundService.scenarios().subscribe({
            next: (scenarios: XxFormLogicScenarioDto[]) => {
                this.scenarios = scenarios ?? [];
                this.restoreSelection();
                this.busy = false;
                this.initialized = true;
            },
            error: () => {
                this.errorMessage = 'Der Szenario-Katalog konnte nicht geladen werden. '
                    + 'Läuft das Backend?';
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /**
     * Re-selects the previously selected scenario after a reload (its `typeId` may have changed
     * when the seed recreated the row), otherwise selects the first seeded one.
     */
    protected restoreSelection(): void {

        const previousKey = this.selected?.key;
        const match = previousKey
            ? this.scenarios.find(scenario => scenario.key === previousKey)
            : null;

        const target = match ?? this.scenarios.find(scenario => scenario.seeded) ?? null;

        this.selected = null;
        if (target) {
            this.selectScenario(target);
        }
    }

    /** True when no scenario is installed yet — the page then offers the seed instead of a form. */
    get needsSeed(): boolean {
        return this.initialized && !this.busy && !this.scenarios.some(scenario => scenario.seeded);
    }

    /**
     * The rule whose `lg.Logic` is currently opened in the object component, or null while only the
     * inline scripts are shown. Reset on every scenario change so a stale logic cannot stay on screen
     * next to a different scenario's form.
     */
    openedRule: XxFormLogicRuleDto | null = null;

    /** Selects a scenario and rebuilds the create form for it. */
    selectScenario(scenario: XxFormLogicScenarioDto): void {

        if (!scenario.seeded || scenario.typeId === null) {
            return;
        }

        this.selected = scenario;
        this.openedRule = null;
        this.rebuildForm();
    }

    /**
     * Opens (or closes) the `lg.Logic` object component for one rule. Clicking the opened rule again
     * closes it, so the form stays reachable without scrolling past a large logic view.
     */
    toggleRule(rule: XxFormLogicRuleDto): void {

        if (rule.logicId === null) {
            return;
        }

        this.openedRule = this.openedRule?.key === rule.key ? null : rule;
    }

    /**
     * German one-liner describing what a rule targets — the same information the `cc.FormLogic` row
     * carries, condensed so the table stays readable.
     */
    ruleTarget(rule: XxFormLogicRuleDto): string {

        if (rule.scope === 'field') {
            return `${rule.targetField ?? '?'} · ${rule.concern ?? '?'}`;
        }

        return 'Direktivliste (beliebige Felder)';
    }

    /** Switches the instance family and rebuilds the create form through the other route. */
    selectFamily(family: XxFormLogicFamily): void {
        this.activeFamily = family;
        this.rebuildForm();
    }

    /**
     * Rebuilds the create DTO and forces a fresh form instance.
     *
     * `typeDtoId` is the reference field id of the instance's `type` association — the id space the
     * form itself uses. Pre-setting it puts the form straight into the state where the type is
     * known, which is what makes the logics apply on the very first render.
     */
    protected rebuildForm(): void {

        if (!this.selected || this.selected.typeId === null) {
            this.createDto = null;
            return;
        }

        this.createDto = new DtoDetail({typeDtoId: this.selected.typeId} as Partial<DtoDetail>);
        this.formInstanceKey++;
    }

    /** Runs the idempotent seed with `force=true` and reloads the catalogue afterwards. */
    runSeed(): void {

        this.busy = true;
        this.errorMessage = null;
        this.seedMessage = null;

        this.playgroundService.seed(true).subscribe({
            next: (result: any) => {
                this.seedMessage = 'Seed ausgeführt: ' + (result?.scenarioCount ?? 0) + ' Szenarien.';
                this.refreshComponent();
            },
            error: () => {
                this.errorMessage = 'Der Seed ist fehlgeschlagen. Details stehen im Server-Log.';
                this.busy = false;
            }
        });
    }

    /** German label of a scenario's mode. */
    modeLabel(scenario: XxFormLogicScenarioDto): string {
        return MODE_LABEL[scenario.mode] ?? scenario.mode;
    }

    /** PrimeNG tag severity of a scenario's mode. */
    modeSeverity(scenario: XxFormLogicScenarioDto): XxTagSeverity {
        return MODE_SEVERITY[scenario.mode] ?? 'secondary';
    }
}
